import { ReactNode, MouseEvent } from 'react';
import cn from 'classnames';
import {
  SortDescriptor,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  TableProps,
  Tooltip,
} from '@ds';
import { HiMenuAlt2, HiSortDescending, HiSortAscending } from 'react-icons/hi';
import { MdInfoOutline } from 'react-icons/md';
import { TablePagination } from './components/TablePagination';

const getSortIcon = (sortBy: SortDescriptor | undefined, columnKey: string): ReactNode => {
  const isSortByCurrentColumn = sortBy?.column === columnKey;

  if (!sortBy || !isSortByCurrentColumn) {
    return <HiMenuAlt2 size={20} />;
  }

  return sortBy.direction === 'ascending' ? <HiSortAscending size={20} /> : <HiSortDescending size={20} />;
};

export type TableColumnType = {
  key: string;
  label: string | ReactNode;
  isSortable: boolean;
  minWidthClassName?: string;
  tooltipText?: string;
};
export type PartialTableColumnType = Omit<TableColumnType, 'isSortable'>;

type BaseTableProps = Pick<
  TableProps,
  | 'baseRef'
  | 'bottomContent'
  | 'hideHeader'
  | 'selectionMode'
  | 'onSelectionChange'
  | 'isStriped'
  | 'selectedKeys'
  | 'disabledKeys'
  | 'disallowEmptySelection'
> & {
  emptyContent?: ReactNode;
};

type DataTableInfiniteProps<T> = {
  idKey?: keyof T;
  type: 'infinite';
  hidePagination?: true;
  label: string;
  dataName?: string;
  columns: TableColumnType[];
  renderCell: (item: T, columnKey: string) => ReactNode;
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  hidePadding?: boolean;
  wrapperClassName?: string;
  baseClassName?: string;
  thClassName?: string;
  tdClassName?: string;
  sortBy?: SortDescriptor | undefined;
  onSortByChange?: (sortBy: SortDescriptor) => void;
  onRowClick?: (e: MouseEvent<HTMLTableRowElement>, data: T) => void;
  isHeaderSticky?: boolean;
} & BaseTableProps;

type DataTablePaginationProps<T> = {
  idKey?: keyof T;
  type?: 'default';
  hidePagination?: boolean;
  label: string;
  dataName?: string;
  columns: TableColumnType[];
  renderCell: (item: T, columnKey: string) => ReactNode;
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  hidePadding?: boolean;
  totalItems: number;
  totalPages: number;
  page: number;
  itemsPerPage: number;
  sortBy?: SortDescriptor;
  wrapperClassName?: string;
  baseClassName?: string;
  thClassName?: string;
  tdClassName?: string;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (page: number) => void;
  onSortByChange: (sortBy: SortDescriptor) => void;
  onRowClick?: (e: MouseEvent<HTMLTableRowElement>, data: T) => void;
  isHeaderSticky?: boolean;
} & BaseTableProps;

type DataTableProps<T> = DataTablePaginationProps<T> | DataTableInfiniteProps<T>;

export const DataTable = <T extends Record<string, unknown>>({
  idKey = 'id',
  type = 'default',
  hidePagination = false,
  label,
  dataName,
  columns,
  renderCell,
  data,
  isLoading,
  isFetching,
  hidePadding = false,
  baseRef,
  bottomContent,
  wrapperClassName,
  baseClassName,
  thClassName,
  tdClassName,
  sortBy = undefined,
  onSortByChange = undefined,
  hideHeader,
  selectionMode = 'none',
  isStriped = true,
  selectedKeys,
  disabledKeys,
  disallowEmptySelection = true,
  onSelectionChange,
  onRowClick,
  isHeaderSticky = false,
  emptyContent = 'Nothing found',
  ...props
}: DataTableProps<T>) => {
  const renderPagination = () => {
    if (type === 'default' && !hidePagination) {
      const { totalItems, totalPages, page, itemsPerPage, onPageChange, onItemsPerPageChange } =
        props as DataTablePaginationProps<T>;

      return (
        <TablePagination
          isLoading={isLoading}
          totalItems={totalItems}
          totalPages={totalPages}
          page={page}
          itemsPerPage={itemsPerPage}
          dataName={dataName}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      );
    }

    return null;
  };

  const tableClassNames = {
    wrapper: cn({ 'p-0 shadow-none': hidePadding }, wrapperClassName),
    th: cn('bg-base-70', thClassName),
    tr: cn('data-[disabled=true]:text-base-5 data-[disabled=true]:opacity-60', { 'cursor-pointer': onRowClick }),
    td: cn(
      'h-[57px]',
      {
        'group-data-[odd=true]/tr:before:bg-base-90': isStriped,
        'group-data-[selected=true]/tr:before:bg-default-200': isStriped,
        'cursor-pointer': !isStriped,
      },
      tdClassName,
    ),
    sortIcon: 'hidden',
    base: baseClassName,
  };

  return (
    <div className="flex flex-col flex-1 items-stretch gap-6">
      <Table
        baseRef={baseRef}
        bottomContent={bottomContent}
        aria-label={label}
        selectionMode={selectionMode}
        selectedKeys={selectedKeys}
        disabledKeys={disabledKeys}
        isStriped={isStriped}
        hideHeader={hideHeader}
        sortDescriptor={sortBy}
        onSortChange={onSortByChange}
        onSelectionChange={onSelectionChange}
        disallowEmptySelection={disallowEmptySelection}
        isHeaderSticky={isHeaderSticky}
        classNames={tableClassNames}
        checkboxesProps={{
          classNames: {
            wrapper: 'after:bg-foreground after:text-background text-background',
          },
          size: 'sm',
        }}
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn
              isRowHeader
              className={column.minWidthClassName}
              key={column.key}
              align="start"
              allowsSorting={column.isSortable}
            >
              <div className="flex gap-2 items-center text-base-20">
                <span className="text-s-sb uppercase">{column.label}</span>
                {column.isSortable && getSortIcon(sortBy, column.key)}
                {column.tooltipText?.length && (
                  <Tooltip content={column.tooltipText} showArrow>
                    <div>
                      <MdInfoOutline size={20} />
                    </div>
                  </Tooltip>
                )}
              </div>
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody
          className="h-full"
          items={data}
          emptyContent={emptyContent}
          loadingContent={<Spinner />}
          loadingState={isFetching ? 'loading' : 'idle'}
        >
          {(item) => (
            <TableRow key={`${item[idKey]}`} onClick={onRowClick ? (e) => onRowClick(e, item) : undefined}>
              {(columnKey) => <TableCell>{renderCell(item, columnKey as string)}</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {renderPagination()}
    </div>
  );
};
